import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import DeviceVisibilityPanel from './DeviceVisibilityPanel'

/**
 * Reads the offerable set of a widget and hands it to the two-column panel, writing the chosen
 * hidden list back into that widget instance's configuration.
 *
 * The load lives here, and with it the three states RN27 treats as NOT reliable — in flight,
 * failed, empty. In any of them the panel offers nothing to move, so no reconciled list can be
 * written and the stored one survives untouched.
 *
 * The `offerableKey` is what decides when to read again: the Device selector always reads the same
 * plant catalogue, the Device Group reads the devices of the group it currently has. The loader
 * itself is deliberately NOT a dependency — an inline arrow would reload on every render.
 */
const DeviceVisibilityConfiguration = ({
  data,
  setData,
  t_,
  loadOfferableDevices,
  offerableKey,
  emptyOfferableMessage
}) => {
  const [offerableDevices, setOfferableDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const loadRef = useRef(loadOfferableDevices)
  loadRef.current = loadOfferableDevices

  useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      try {
        const result = await loadRef.current()
        if (mounted) {
          setOfferableDevices(Array.isArray(result) ? result : [])
          setError(false)
        }
      } catch (err) {
        if (mounted) {
          console.error('Error loading devices:', err)
          setOfferableDevices([])
          setError(true)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }
    load()
    return () => { mounted = false }
  }, [offerableKey])

  const handleChange = useCallback((hiddenDeviceIds) => {
    setData({ ...(data ?? {}), hiddenDeviceIds })
  }, [data, setData])

  return (
    <DeviceVisibilityPanel
      offerableDevices={offerableDevices}
      loading={loading}
      error={error}
      hiddenDeviceIds={data?.hiddenDeviceIds}
      onChange={handleChange}
      t_={t_}
      emptyOfferableMessage={emptyOfferableMessage}
    />
  )
}

DeviceVisibilityConfiguration.propTypes = {
  /** The configuration of the widget instance being edited; may be null or lack the key (RN24). */
  data: PropTypes.object,
  setData: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired,
  /** Reads the offerable set. Already deduplicated by the caller when its source needs it (RN35). */
  loadOfferableDevices: PropTypes.func.isRequired,
  /** Reading it again is worth it only when this changes. */
  offerableKey: PropTypes.string,
  emptyOfferableMessage: PropTypes.string.isRequired
}

DeviceVisibilityConfiguration.defaultProps = {
  offerableKey: ''
}

export default DeviceVisibilityConfiguration
